import { Select } from 'antd';
import axios from 'axios';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { auditorNameSearchApi } from '../../Config/config';
import '../../index.css';
import { Option } from 'antd/es/mentions';
import { PeFilterContext } from '../../Contexts/PeFilterContext';
import { UserContext } from '../../Contexts/UserContext';

/**
 * The `tagRender` function renders a tag component with a label, value, and a close button.
 */
const tagRender = (props) => {
  const { label, value, closable, onClose } = props;
  return (
    <div className="mx-1 my-1 text-[12px] flex items-stretch justify-between font-bold px-1 py-1 rounded-sm bg-slate-100 text-black border">
      <div>{label}</div>
      <div onClick={onClose} className='px-1 flex items-center justify-center cursor-pointer'>
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="black" className="w-3 h-3">
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </div>
    </div>
  );
};

/**
 * The `AuditorNameFilter` component is a searchable input filter for selecting auditor names,
 * with options fetched from an API based on user input.
 */
const AuditorNameFilter = ({ AuditiorNameOptions, customPathname, setAuditiorNameOptions, label = "Auditor Name", isPopupOpen, setIsPopupOpen }) => {
  const { getToken } = useContext(UserContext);
  const { refineSearchFilters } = useContext(PeFilterContext);

  const [fetching, setFetching] = useState(false);
  const [options, setOptions] = useState([]);
  const [searchText, setSearchText] = useState("");
  const firstRender = useRef(false);
  const [open, setOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    setAuditiorNameOptions(refineSearchFilters[customPathname]?.auditorName ?? []);
  }, [customPathname, refineSearchFilters, setAuditiorNameOptions]);

  useEffect(() => {
    let sub;
    if (firstRender.current) {
      sub = setTimeout(() => {
        if (searchText !== "" && searchText?.length > 2) {
          setOptions([]);
          setFetching(true);
          getSearchTags(searchText).then(newOptions => {
            console.log("Received options:", newOptions);
            setOptions(newOptions || []);
            setFetching(false);
          }).catch(error => {
            console.error("Error in getSearchTags:", error);
            setOptions([]);
            setFetching(false);
          });
        } else {
          setOptions([]);
        }
      }, 500);
    } else {
      firstRender.current = true;
      setOptions([]);
    }
    return () => clearTimeout(sub);
  }, [searchText]);

  const handleChange = (value, options) => {
    setAuditiorNameOptions(value?.map(option => ({ id: option, name: option })));
    setOpen(false);
    clearOptions();
  };

  const getSearchTags = async (searchText) => {
    try {
      const response = await axios.post(auditorNameSearchApi, {
        "auditor_name": searchText,
      }, {
        headers: {
          "Authorization": getToken()
        }
      });

      if (response.status === 200) {
        const auditorNames = response.data?.auditor_names || [];
        console.log("API response auditor_names:", auditorNames);
        // Ensure we return an array
        return Array.isArray(auditorNames) ? auditorNames : [];
      } else {
        return [];
      }
    } catch (error) {
      console.error("API Error:", error);
      return [];
    }
  };

  const clearOptions = () => {
    setOptions([]);
    setSearchText("");
  };

  const selectedCount = AuditiorNameOptions?.length ?? 0;
  const hasValue = searchText || selectedCount > 0;

  return (
    <div className="relative w-full md:w-[90%]">
      <div className={`absolute left-3 transition-all duration-200 pointer-events-none flex items-center gap-2 z-10 ${
        isFocused || hasValue || open
          ? 'top-0 -translate-y-1/2 text-xs bg-white px-1 text-[#BC8B20]' 
          : 'top-1/2 -translate-y-1/2 text-sm text-gray-500'
      }`}>
        <span>{label}</span>
        {(isFocused || hasValue || open) && selectedCount > 0 && (
          <span className="bg-white rounded-full px-2 py-0.5 text-xs font-medium border border-gray-400">
            {selectedCount}
          </span>
        )}
      </div>

      <div className="relative border border-gray-400 shadow-md rounded-lg bg-white">
        <Select
          mode="multiple"
          filterOption={false}
          style={{
            width: '100%',
            borderRadius: '8px',
            background: '#ffffff',
          }}
          className="text-[#333333] auditor-select-custom"
          open={open}
          onDropdownVisibleChange={(visible) => {
            setOpen(visible);
            setIsFocused(visible);
          }}
          bordered={false}
          tagRender={tagRender}
          onSearch={(value) => setSearchText(value)}
          onChange={handleChange}
          value={AuditiorNameOptions?.map(auditor => auditor.name)}
          onBlur={clearOptions}
          virtual={false}
          notFoundContent={searchText === "" || searchText?.length < 3 ? null : fetching ? <p className='text-[#333333] text-[13px]'>Searching...</p> : <p className='text-[#333333] text-[13px]'>No data found</p>}
        >
          {Array.isArray(options) && options?.map((option, index) => {
            const optionValue = typeof option === 'string' ? option : option.name;
            return <Option key={`${optionValue}-${index}`} value={optionValue}>{optionValue}</Option>
          })}
        </Select>
      </div>

      <style jsx>{`
        .auditor-select-custom :global(.ant-select-selector) {
          border: none !important;
          box-shadow: none !important;
          padding: 8px 16px !important;
          min-height: 40px !important;
          max-height: 40px !important;
          overflow: hidden !important;
        }
        .auditor-select-custom :global(.ant-select-selection-search-input) {
          height: auto !important;
        }
        .auditor-select-custom :global(.ant-select-selection-overflow) {
          flex-wrap: nowrap !important;
          overflow: hidden !important;
        }
        @media (min-width: 768px) {
          .auditor-select-custom :global(.ant-select-selector) {
            max-height: none !important;
            overflow: visible !important;
          }
          .auditor-select-custom :global(.ant-select-selection-overflow) {
            flex-wrap: wrap !important;
            overflow: visible !important;
          }
        }
      `}</style>
    </div>
  );
};

export default AuditorNameFilter;